import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Alert, Modal } from 'react-bootstrap-5';
import { connect } from 'react-redux';
import { find, some } from 'lodash';
import { isBlank } from 'helpers/string_helpers';

import AssociatedContactSelector from 'components/tasks/associated_contact_selector';
import contactRolePropTypes from 'bundles/project/prop_types/contact_roles';
import NewOrEditContactForm from 'components/contacts/new_or_edit_contact_form';

export const CONTACT_OPTIONS = {
  person: { label: 'Contact', model: 'Contacts::Person' },
  company: { label: 'Company', model: 'Contacts::Company' }
};

export function ContactSelector({
  availableDatabaseOptions,
  contactRoles,
  personOrCompany,
  projectId,
  selectedContactRole,
  show,
  onCancel,
  onSave
}) {
  const [contact, setContact] = useState();
  const [errors, setErrors] = useState();
  const [relation, setRelation] = useState(contactRoles[0]);
  const [showCreateContact, setShowCreateContact] = useState(false);

  useEffect(() => {
    if (show) {
      setContact(selectedContactRole || null);
      setRelation(selectedContactRole ? getContactRelationship(selectedContactRole) : contactRoles[0]);
    }

    return () => {
      setContact(null);
      setErrors(null);
      setRelation(contactRoles[0]);
      setShowCreateContact(false);
    };
  }, [show]);

  const anyAvailableDatabases = () => availableDatabaseOptions.length > 0;
  const getContactRelationship = (contact) => {
    if (!contact) return;

    return find(contactRoles, cr => cr.value === contact.relationship);
  };

  const isNewContactWithoutDatabase = () => !selectedContactRole && !anyAvailableDatabases();
  const isNewNoDbOrEditForm = () => selectedContactRole || isNewContactWithoutDatabase();

  const cancelCreateContact = () => {
    setShowCreateContact(false);
    setContact(null);
    isNewNoDbOrEditForm() && onCancel();
  };

  const handleCreateContact = (contact) => {
    setShowCreateContact(false);
    setContact({ ...contact, name: contact.label });
    isNewNoDbOrEditForm() && handleSave({
      associatedModelId: contact.associatedModelId,
      associatedModelType: contact.associatedModelType,
      name: contact.label
    });
  };

  const hideModal = () => {
    setShowCreateContact(false);
    onCancel();
  };

  const modelAdded = contact => setContact(contact);

  const handleRelationChange = event => setRelation(event);

  const handleSave = (contact) => {
    const data = { ...contact, relation: relation.value };

    onSave(data).catch((errors) => {
      if (!some(errors, err => err.includes('relationship'))) {
        errors = ['This project is not valid. Please fix it in the edit form before adding contacts.'];
      }
      setErrors(errors);
    });
  };

  const editContactProps = () => {
    if (!selectedContactRole) return;

    return {
      contactId: selectedContactRole.contactId,
      contactType: selectedContactRole.contactType,
      hasCrm: anyAvailableDatabases()
    };
  };

  const newContactProps = () => {
    return {
      availableDatabaseOptions: availableDatabaseOptions,
      personOrCompany: personOrCompany,
      projectId: projectId
    };
  };

  const renderRelationInput = () => {
    return (
      <React.Fragment>
        <label>Relation</label>
        <Select
          name="relation"
          options={contactRoles}
          value={relation}
          onChange={handleRelationChange}
        />
      </React.Fragment>
    );
  };

  const renderErrors = () => {
    if (isBlank(errors)) return null;

    let messages = null;

    if (Array.isArray(errors)) {
      messages = errors.map((error, i) => <li key={i}>{error}</li>);
    }

    return (
      <Alert name="errors-alert" variant="warning">
        <div className="mb-2"><strong>Whoops -</strong> There were errors in the form that prevented saving.</div>
        <ul>{messages}</ul>
      </Alert>
    );
  };

  const renderContactForm = () => (
    <NewOrEditContactForm
      additionalFields={isNewNoDbOrEditForm() && renderRelationInput()}
      editProps={editContactProps()}
      newProps={newContactProps()}
      personOrCompany={personOrCompany}
      showNewFormTypeSelector={!personOrCompany}
      onCancel={cancelCreateContact}
      onSave={handleCreateContact}
    />
  );

  const renderSelectContact = () => {
    return (
      <div name="select-contact-form">
        <label>{personOrCompany ? CONTACT_OPTIONS[personOrCompany].label : 'Contact'}</label>
        <AssociatedContactSelector
          autoFocus
          className="mb-4"
          selectedValue={contact}
          type={personOrCompany && CONTACT_OPTIONS[personOrCompany].model}
          onCreateContact={() => setShowCreateContact(true)}
          onModelAdded={modelAdded}
        />
        {renderRelationInput()}

        <hr />
        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-ghost mr-4" name="cancel" onClick={hideModal}>Cancel</button>
          <button className="btn btn-primary" name="save" onClick={() => handleSave(contact)}>Save</button>
        </div>
      </div>
    );
  };

  const renderModalBody = () => {
    if (showCreateContact || isNewNoDbOrEditForm()) return renderContactForm();

    return renderSelectContact();
  };

  return (
    <Modal className="contact-modal overflow-visible" show={show} size="lg" onHide={hideModal}>
      <Modal.Header closeButton>
        <Modal.Title>
          {
            (selectedContactRole ? 'Edit ' : 'Add a ') +
            (personOrCompany ? CONTACT_OPTIONS[personOrCompany].label : 'Contact')
          }
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {renderErrors()}
        {renderModalBody()}
      </Modal.Body>
    </Modal>
  );
}

ContactSelector.propTypes = {
  availableDatabaseOptions: PropTypes.arrayOf(PropTypes.array),
  contactRoles: contactRolePropTypes,
  personOrCompany: PropTypes.oneOf(['person', 'company']),
  projectId: PropTypes.number,
  selectedContactRole: PropTypes.shape({
    contactId: PropTypes.number.isRequired,
    contactType: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    relationship: PropTypes.string.isRequired
  }),
  show: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

const mapStateToProps = ({ contactRoles, availableDatabaseOptions, project }) => {
  return { contactRoles, availableDatabaseOptions, projectId: project.id };
};

export default connect(mapStateToProps)(ContactSelector);
